import { Grid2, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import BaseProfileCard from '@components/ProfileCard';

import { useSession } from '@hooks/useSession';

import type { PatientRecord } from '@models/patient';

import { calculateAge, convertDateToUTC } from '@utils/date';
import { formatDate, formatName, formatPhone } from '@utils/format';

const messages = defineMessages({
  address: {
    id: 'healthSummary.address',
    defaultMessage: 'Address',
  },
  phone: {
    id: 'healthSummary.phone',
    defaultMessage: 'Phone',
  },
  email: {
    id: 'healthSummary.email',
    defaultMessage: 'Email',
  },
});

interface ProfileCardProps {
  patient?: PatientRecord | null;
  loading: boolean;
  isSharing?: boolean;
}

export default function ProfileCard({
  patient,
  loading,
  isSharing,
}: ProfileCardProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();

  const dob = patient?.getBirthday();
  const gender = patient?.gender;

  const profileCardSubtitle = `
    ${
      dob
        ? `${formatDate({ date: dob })} (${
            dob ? calculateAge(convertDateToUTC(new Date(dob))) : '-'
          } years old)`
        : ''
    }
     ${gender && dob ? ' - ' : ''}
     ${gender ?? ''}
  `;

  const patientInfo = (
    <ContentLoader lineCount={1} loading={loading}>
      <Grid2 container spacing={1}>
        <Grid2 size={{ xs: 12 }}>
          <Typography color="primary.main" variant="subtitle2">
            {formatMessage(messages.address)}
          </Typography>
          <Typography color="text.primary" variant="body1">
            {patient?.primaryAddress ?? '- -'}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Typography color="primary.main" variant="subtitle2">
            {formatMessage(messages.phone)}
          </Typography>
          <Typography color="text.primary" variant="body1">
            {formatPhone(patient?.phone) || '- -'}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Typography color="primary.main" variant="subtitle2">
            {formatMessage(messages.email)}
          </Typography>
          <Typography
            color="text.primary"
            variant="body1"
            sx={{ wordBreak: 'break-word' }}
          >
            {patient?.email || '- -'}
          </Typography>
        </Grid2>
      </Grid2>
    </ContentLoader>
  );

  return (
    <BaseProfileCard
      name={formatName(patient?.first_name, patient?.last_name)}
      profile_picture={isSharing ? null : activeProfile?.profile_picture}
      loading={loading}
      subtitle={profileCardSubtitle}
    >
      {patientInfo}
    </BaseProfileCard>
  );
}
